import { EventHandler } from '../event-handler'

/**
 * Class representing a transformer that converts event payload keys to snake_case.
 * This class extends EventHandler and provides functionality to transform event payloads.
 * @extends EventHandler
 */
export class EventPayloadSnakeCaseTransformer extends EventHandler {
  /**
   * Transforms a string to snake_case.
   * @param {string} value - The input string to be transformed.
   * @returns {string} The transformed string in snake_case.
   */
  transformToSnakeCase(value) {
    return value
      .replaceAll(/([a-z])([A-Z])/g, '$1_$2')
      .replaceAll(/([0-9])([A-Z])/g, '$1_$2')
      .replaceAll(/([A-Z])([0-9])/g, '$1_$2')
      .replaceAll(/([a-z])([0-9])/g, '$1_$2')
      .replaceAll(/([0-9])([a-z])/g, '$1_$2')
      .toLowerCase()
      .replace(/\W+/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^_|_$/g, '')
  }

  /**
   * Transforms all keys of an object to snake_case.
   * @param {Record<string, any>} object - The input object with keys to be converted.
   * @returns {Record<string, any>} A new object with the keys converted to snake_case.
   */
  transformKeysToSnakeCase(object) {
    const transformedEntries = Object.entries(object).map(([key, value]) => {
      return [this.transformToSnakeCase(key), value]
    })

    return Object.fromEntries(transformedEntries)
  }

  /**
   * Handles an event by transforming its payload keys to snake_case.
   * @param {import('../event').Event} event - The event to be handled.
   * @override
   */
  handle(event) {
    const payload = event.getPayload()
    const transformedPayload = this.transformKeysToSnakeCase({ ...payload })

    event.setPayload(transformedPayload)

    super.handle(event)
  }
}
